/**
 * ProductsView Component - Products tab content (cards and list views)
 * Shows products from ProductoCliente table with GTIN/EAN codes
 */
import React from 'react';
import { Package, Database, AlertTriangle } from 'lucide-react';
import Barcode from 'react-barcode';
import { Product } from '../../services/productService';
import { ClickableBarcode } from './ClickableBarcode';

// Validate barcode - any numeric string with 8+ digits
const isValidBarcode = (value: string): boolean => {
  return /^\d{8,}$/.test(value);
};

interface ProductsViewProps {
  products: Product[];
  isAdmin: boolean;
  totalProducts: number;
  viewMode: 'cards' | 'list';
  formatPrice: (price: number) => string;
}

export const ProductsView: React.FC<ProductsViewProps> = ({
  products,
  isAdmin,
  totalProducts,
  viewMode,
  formatPrice,
}) => {
  return (
    <div>
      {/* Info banner */}
      <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        <div className="flex items-start gap-3">
          <Database className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">
              Tabela Fato - Produtos Cadastrados no Sistema
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              {isAdmin
                ? `Lista completa de TODOS os ${totalProducts} produtos cadastrados no banco de dados com seus códigos de barras principais (EAN-13/GTIN).`
                : 'Produtos cadastrados no sistema visíveis para seu perfil.'}
            </p>
            {!isAdmin && (
              <p className="text-xs text-blue-600 mt-2 font-semibold">
                ℹ️ Para visualizar todos os produtos, entre com uma conta de administrador.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cards view */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => {
            const barcodeValue = product.gtin || product.ean || '';  // GTIN-S-002: sem GTIN = vazio
            return (
              <div key={product.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{product.nome}</h3>
                      <p className="text-sm text-gray-600 mb-2">{product.descricao || 'Sem descrição'}</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Código:</span>
                      <span className="font-semibold text-gray-900">{product.codigo}</span>
                    </div>
                    {(product.gtin || product.ean) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">GTIN/EAN:</span>
                        <span className="font-mono text-gray-900">{product.gtin || product.ean}</span>
                      </div>
                    )}
                    {product.categoria && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Categoria:</span>
                        <span className="text-gray-900">{product.categoria}</span>
                      </div>
                    )}
                    {product.fabricante && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fabricante:</span>
                        <span className="text-gray-900">{product.fabricante}</span>
                      </div>
                    )}
                    {(product.preco_unitario || product.preco) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Preço:</span>
                        <span className="font-bold text-green-600">{formatPrice(product.preco_unitario || product.preco || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Unidade:</span>
                      <span className="text-gray-900">{product.unidade}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 mt-4 pt-4">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">
                        EAN-13 / GTIN
                      </span>
                      <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 flex items-center justify-center">
                        {isValidBarcode(barcodeValue) ? (
                          <ClickableBarcode productName={product.nome}>
                            <Barcode
                              value={barcodeValue}
                              format="CODE128"
                              width={1.5}
                              height={50}
                              displayValue={true}
                              fontSize={12}
                              margin={0}
                            />
                          </ClickableBarcode>
                        ) : (
                          <div className="flex flex-col items-center gap-2 py-2">
                            <AlertTriangle className="w-8 h-8 text-yellow-500" />
                            <span className="text-xs text-yellow-600 text-center">
                              Código de barras indisponível
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GTIN</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fabricante</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidade</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{product.nome}</div>
                      {product.descricao && (
                        <div className="text-sm text-gray-500">{product.descricao}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">{product.codigo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">{product.gtin || product.ean || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.categoria || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.fabricante || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.cliente_nome_fantasia || product.cliente_razao_social || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        {(product.preco_unitario || product.preco) ? formatPrice(product.preco_unitario || product.preco || 0) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.unidade}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {products.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum produto cadastrado no sistema.</p>
        </div>
      )}
    </div>
  );
};
